import { cn } from "lib/utils";
import React from "react";

import deliveriesDropdown from "assets/images/totem/deliveries-dropdown.svg";
import { TotemDelivery, TotemScheduling } from "store/ducks/totem-scheduling";

type SchedulingDetailsCardProps = {
	scheduling: TotemScheduling;
	compact?: boolean;
};

const getRelationLabel = (relation: unknown, fallback = "—") => {
	if (!relation || typeof relation !== "object") return fallback;
	const value = relation as Record<string, unknown>;
	return String(
		value.name ||
			value.company_name ||
			value.description ||
			value.code ||
			fallback,
	);
};

const formatTime = (value: string | null) => {
	if (!value) return "—";
	const match = value.match(/(?:T|\s)(\d{2}:\d{2})/);
	return match?.[1] || value;
};

const formatDateTime = (value: string | null) => {
	if (!value) return "—";

	const match = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:T|\s)(\d{2}:\d{2})/);

	if (!match) return value;

	const [, year, month, day, time] = match;
	return `${day}/${month}/${year} ${time}`;
};

const formatCpf = (value: string | null) => {
	if (!value) return "—";
	const digits = value.replace(/\D/g, "");
	return digits.length === 11
		? digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
		: value;
};

const formatWeight = (value: number | string | null | undefined) => {
	if (value === null || value === undefined || value === "") return "—";

	const numericValue = Number(value);
	if (Number.isNaN(numericValue)) return String(value);

	return `${numericValue.toLocaleString("pt-BR")} kg`;
};

const Detail = ({
	label,
	value,
	className,
	valueClassName,
	compact = false,
}: {
	label: string;
	value: string;
	className?: string;
	valueClassName?: string;
	compact?: boolean;
}) => (
	<div
		className={cn(
			"flex min-w-0 flex-1 flex-col gap-2",
			compact && "gap-1",
			className,
		)}
	>
		<span
			className={cn(
				"font-GilroySemibold text-[16px] uppercase tracking-[0.8px] text-[#6A7282] max-[639px]:text-[12px] max-[639px]:tracking-[0.6px]",
				compact && "text-[10px] tracking-[0.5px]",
			)}
		>
			{label}
		</span>
		<span
			className={cn(
				"break-words font-GilroyBold text-[32px] leading-8 text-[#1E2939] max-[639px]:text-[16px] max-[639px]:leading-5",
				valueClassName,
				compact &&
					"text-[16px] leading-5 max-[639px]:text-[14px] max-[639px]:leading-4",
			)}
		>
			{value}
		</span>
	</div>
);

const DeliveriesSection: React.FC<{
	deliveries?: TotemDelivery[];
	compact?: boolean;
}> = ({ deliveries = [], compact = false }) => {
	if (!deliveries.length) return null;

	return (
		<div
			className={cn(
				"flex w-full flex-col gap-3 border-t border-[#D1D5DC] pt-6 max-[639px]:gap-2 max-[639px]:pt-4",
				compact && "gap-2 pt-4 max-[639px]:pt-3",
			)}
		>
			<details className={cn("group w-full")}>
				<summary
					className={cn(
						"grid cursor-pointer list-none grid-cols-[1fr_auto] items-center gap-4 border-b border-[#D1D5DC] pb-3 font-GilroySemibold text-[12px] uppercase tracking-[0.6px] text-[#6A7282] marker:hidden [&::-webkit-details-marker]:hidden",
						compact && "pb-2 text-[12px] tracking-[0.6px]",
					)}
				>
					<span>Deliveries ({deliveries.length})</span>
					<img
						src={deliveriesDropdown}
						alt=""
						className={cn(
							"h-8 w-8 transition-transform group-open:rotate-180",
							compact && "h-7 w-7",
						)}
					/>
				</summary>
				<div className={cn("flex w-full flex-col")}>
					{deliveries.map((delivery, index) => (
						<div
							key={`${delivery.code}-${index}`}
							className={cn(
								"grid grid-cols-2 gap-4 border-b border-[#E5E7EB] py-4 last:border-b-0 max-[639px]:grid-cols-1 max-[639px]:gap-3",
								compact && "gap-3 py-3 max-[639px]:gap-2",
							)}
						>
							<div
								className={cn(
									"col-span-2 flex min-w-0 flex-col gap-1 max-[639px]:col-span-1",
								)}
							>
								<span
									className={cn(
										"font-GilroySemibold text-[11px] uppercase tracking-[0.5px] text-[#6A7282]",
										compact && "text-[9px]",
									)}
								>
									Delivery
								</span>
								<span
									className={cn(
										"break-words font-GilroyBold text-[16px] leading-5 text-[#1E2939]",
										compact && "text-[12px] leading-4",
									)}
								>
									{delivery.code || "—"}
								</span>
							</div>
							{[
								{
									label: "Peso bruto",
									value: formatWeight(delivery.gross_weight),
								},
								{ label: "Origem", value: delivery.origin },
								{
									label: "Destino",
									value: delivery.destination,
								},
								{
									label: "Transportadora",
									value: delivery.carrier,
								},
								{ label: "Armazém", value: delivery.warehouse },
							].map(({ label, value }) => (
								<div
									key={label}
									className={cn(
										"flex min-w-0 flex-col gap-1",
									)}
								>
									<span
										className={cn(
											"font-GilroySemibold text-[11px] uppercase tracking-[0.5px] text-[#6A7282]",
											compact && "text-[9px]",
										)}
									>
										{label}
									</span>
									<span
										className={cn(
											"break-words font-sans text-[14px] leading-5 text-[#1E2939]",
											compact && "text-[11px] leading-4",
										)}
									>
										{value || "—"}
									</span>
								</div>
							))}
						</div>
					))}
				</div>
			</details>
		</div>
	);
};

export const SchedulingDetailsCard: React.FC<SchedulingDetailsCardProps> = ({
	scheduling,
	compact = false,
}) => (
	<div
		className={cn(
			"flex w-full flex-col gap-6 bg-white max-[639px]:gap-4",
			compact ? "p-0" : "p-6",
		)}
	>
		<div
			className={cn(
				"flex w-full items-start justify-between gap-4 border-b border-[#D1D5DC] pb-6 max-[639px]:pb-4",
				compact && "pb-4 max-[639px]:pb-3",
			)}
		>
			<Detail
				label="Ordem de coleta"
				value={String(scheduling.order_number || scheduling.id)}
				valueClassName="font-sans font-bold"
				compact={compact}
			/>
			<Detail
				label="Peso"
				value={formatWeight(scheduling.weight)}
				compact={compact}
			/>
		</div>
		<div
			className={cn(
				"flex w-full items-start justify-between gap-4 border-b border-[#D1D5DC] pb-6 max-[639px]:pb-4",
				compact && "pb-4 max-[639px]:pb-3",
			)}
		>
			<Detail
				label="Placa"
				value={scheduling.vehicle_plate || "—"}
				valueClassName="font-sans font-bold"
				compact={compact}
			/>
			<Detail
				label="CPF"
				value={formatCpf(scheduling.driver_document)}
				compact={compact}
			/>
		</div>
		<div
			className={cn(
				"flex w-full items-start justify-between gap-4 border-b border-[#D1D5DC] pb-6 max-[639px]:pb-4",
				compact && "pb-4 max-[639px]:pb-3",
			)}
		>
			<Detail
				label="Doca"
				value={getRelationLabel(scheduling.dock)}
				valueClassName="font-sans font-bold"
				compact={compact}
			/>
			<Detail
				label="Data/Hora"
				value={formatDateTime(scheduling.starts_at)}
				compact={compact}
			/>
		</div>
		<div
			className={cn(
				"flex w-full flex-col gap-6 pb-6 max-[639px]:gap-4 max-[639px]:pb-4",
				compact && "gap-3 pb-4 max-[639px]:gap-2 max-[639px]:pb-3",
			)}
		>
			<Detail
				label="Motorista"
				value={(
					scheduling.driver_name ||
					getRelationLabel((scheduling as any).driver)
				).toUpperCase()}
				className="w-full"
				valueClassName="text-[24px]"
				compact={compact}
			/>
			<Detail
				label="Transportadora"
				value={getRelationLabel(scheduling.carrier).toUpperCase()}
				className="w-full"
				valueClassName="text-[24px]"
				compact={compact}
			/>
			<DeliveriesSection
				deliveries={scheduling.deliveries}
				compact={compact}
			/>
		</div>
		{!compact && (
			<div className={cn("flex w-full flex-col gap-6 max-[639px]:gap-4")}>
				<Detail
					label="Origem"
					value={scheduling.origin || "—"}
					valueClassName="text-[24px]"
				/>
				<Detail
					label="Destino"
					value={scheduling.destination || "—"}
					valueClassName="text-[24px]"
				/>
			</div>
		)}
	</div>
);

export const formatSchedulingTime = formatTime;
